// Lazy stand-ins for components most visitors never need, split into bundles fetched the first time one is rendered:
//   ui     (entry ui-main.js):     base components the first screen does not render (dialogs, menus, text fields, tabs...)
//   extra  (entry extra-main.js):  signed-in pages and dialogs (admin console, account page, apps, sign-in, user menu, theme picker)
//   media  (entry media-main.js):  video and photo sections
//   editor (entry editor-main.js): everything only admins in edit mode see (study editor, section dialog, doodle editor, form controls)
// Before any of them runs, the second half of the MUI build (/shared/mui/more.js) is merged into window.MaterialUI.
// Each stand-in registers under the real name and stays registered after its bundle loads (the real component is kept aside),
// so a component's type never changes: a swap would remount it (losing state, e.g. focus mid-typing).
// A component file moved into one of these bundles must have its names listed here, or main-bundle code would get undefined.
(function () {
  const { useState, useEffect } = React;
  const loading = {}; // bundle -> Promise
  const real = {};    // name -> real component, once its bundle has loaded
  const owner = {};   // name -> bundle
  const stand = {};   // name -> stand-in

  // The rest of MUI: same directory and version as the core module index.html loads.
  let muiMore = null;
  function loadMuiMore() {
    if (!muiMore) {
      const core = document.querySelector('link[rel=x-app-script][data-module]');
      const url = core ? core.getAttribute('href').replace('core.js', 'more.js') : '/shared/mui/more.js';
      muiMore = import(url).then(m => { Object.assign(window.MaterialUI, m); }, e => { muiMore = null; throw e; });
    }
    return muiMore;
  }

  const DEPS = { extra: ['ui'], media: ['ui'], editor: ['ui'] }; // fetched alongside, so a dialog in them does not wait for a second trip

  function load(bundle) {
    if (!loading[bundle]) {
      (DEPS[bundle] || []).forEach(b => load(b).catch(() => {})); // a failure is retried by the stand-in that needs it
      loading[bundle] = loadMuiMore().then(() => new Promise((resolve, reject) => {
        const main = document.querySelector('script[src*="dist/portal.js"]');
        const s = document.createElement('script');
        s.src = main ? main.getAttribute('src').replace('portal.js', `${bundle}.js`) : `./dist/${bundle}.js`;
        s.onload = () => {
          // The bundle registered its real components: keep them aside and put the stand-ins back.
          Object.keys(owner).forEach(n => {
            if (owner[n] === bundle && window.Components[n] !== stand[n]) { real[n] = window.Components[n]; window.Components[n] = stand[n]; }
          });
          resolve();
        };
        s.onerror = () => { s.remove(); reject(new Error(`${bundle} bundle failed to load`)); };
        document.head.appendChild(s);
      })).catch(e => { delete loading[bundle]; throw e; });
    }
    return loading[bundle];
  }

  // wanted(props) says whether this render needs the real component yet (e.g. a dialog only once it opens). It may call hooks.
  function lazy(bundle, names, wanted = () => true) {
    names.forEach(name => {
      const Lazy = React.forwardRef(function Lazy(props, ref) {
        const [Real, setReal] = useState(() => real[name] || null);
        const want = wanted(props);
        useEffect(() => {
          if (Real || !want) return;
          let live = true;
          load(bundle).then(() => { if (live) setReal(() => real[name]); }).catch(() => {});
          return () => { live = false; };
        }, [want, Real]);
        if (!Real) return null;
        return React.createElement(Real, ref ? { ...props, ref } : props);
      });
      Lazy.displayName = 'Lazy' + name;
      owner[name] = bundle;
      stand[name] = Lazy;
      window.Components[name] = Lazy;
    });
  }

  const open = p => !!p.open;
  lazy('ui', ['Dialog', 'Menu', 'Snackbar'], open);
  lazy('ui', ['PollWidget']);
  lazy('ui', ['DialogTitle', 'DialogContent', 'DialogActions', 'MenuItem', 'ListItemIcon', 'ListItemText',
    'TextField', 'OutlinedTextField', 'FilledTextField', 'SearchBar', 'SearchField', 'Select', 'ExpressiveSlider',
    'Tabs', 'Tab', 'PrimaryTab', 'SecondaryTab', 'FileDrop']);
  lazy('extra', ['AdminConsole', 'AccountPage', 'AppsView', 'WelcomeCard',
    'DataTable', 'DataTableContainer', 'DataTableHead', 'DataTableBody', 'DataTableRow', 'DataTableCell']);
  lazy('extra', ['AuthDialog', 'PagesDialog', 'PasskeyPrompt', 'UserMenu', 'ThemePicker'], open);

  lazy('media', ['VideoWorkspace', 'PhotoWorkspace', 'PhotoImmerse', 'PhotoLightbox', 'VideoLightbox', 'SetPhotoPicker', 'MediaLayout', 'MediaFinderPanel',
    'TagInput', 'Accordion', 'FilterToggleButton']);

  lazy('editor', ['StudyEditor', 'SectionDialog', 'SectionEmpty', 'DoodleEditDialog', 'PositionField', 'EditorBlockCard',
    'EditorToolbar', 'ListEditor', 'TableEditor', 'CarouselEditor', 'IconPicker', 'LinkableTextField', 'FormatButton',
    'Radio', 'RadioGroup', 'Slider', 'Autocomplete', 'NumberField', 'FooterEditor', 'FabEditor', 'PollFields']);
  // Rendered for everyone but shows nothing unless this viewer may edit the doodle: load the editor only for editors.
  lazy('editor', ['DoodleEditButton'], p => window.Doodle.useDoodle(p.target).canEdit);

  window.LazyBundles = { load };
})();
